import { Button } from '@neo/index'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { Tooltip } from '@neo/primitives/tooltip'
import { Bug } from 'lucide-react'
import { useState } from 'react'
import { flowService } from 'v2/shared/services/flow-service'
import { agentLogger } from 'v2/utils/logger'
import { getFlowIdFromUrl } from '../utils'

interface NodeMetaDebugProps {
  nodeId: string
  nodeName: string
}

interface NodeMeta {
  meta: { tasks?: Array<Record<string, unknown>> } | null
  meta_hash: string | null
}

/**
 * Debug-mode (isDebuggingMode flag) inspector for an ai_agent_v2 node's
 * server-derived `_meta` (compiled task graph). `_meta` is hidden from the
 * normal flow projection, so this fetches it on demand from the dedicated
 * config endpoint and renders the raw task graph for inspection.
 */
export const NodeMetaDebug = ({ nodeId, nodeName }: NodeMetaDebugProps) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<NodeMeta | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadMeta = async () => {
    setOpen(true)
    setLoading(true)
    setError(null)
    try {
      const phloId = getFlowIdFromUrl()
      const resp = await flowService.getPhloNodeMeta(phloId, nodeId)
      setData(resp.data)
    } catch (e) {
      agentLogger.error('getPhloNodeMeta failed:', e)
      setError('Failed to load node meta')
    } finally {
      setLoading(false)
    }
  }

  const taskCount = data?.meta?.tasks?.length ?? 0

  return (
    <div className="nodrag">
      <Tooltip content="Inspect compiled _meta (debug)" delayDuration={300}>
        <Button
          variant="ghost"
          size="iconXs"
          data-testid={`button-node-debug-meta-${nodeId}`}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            loadMeta()
          }}
        >
          <Bug size={14} className="text-secondary" />
        </Button>
      </Tooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[680px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Compiled _meta — {nodeName}</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="text-s-400 text-secondary p-4">Loading…</div>
          ) : error ? (
            <div className="text-s-400 text-status-danger p-4">{error}</div>
          ) : (
            <div className="overflow-auto">
              <div className="text-xs-400 text-secondary mb-2">
                {data?.meta
                  ? `${taskCount} task${taskCount === 1 ? '' : 's'} · hash ${(data.meta_hash || '').slice(0, 12) || 'n/a'}`
                  : 'No _meta compiled yet — runtime falls back to the free-form agent.'}
              </div>
              <pre className="text-xxs-400 bg-bg2 rounded-md p-3 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(data?.meta ?? null, null, 2)}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
